"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  SimpleGrid,
  Group,
  Box,
  Anchor,
  Card,
} from "@mantine/core";
import Image from "next/image";
import HairShadeShortlist from "@/components/HairShadeShortlist";
import PaletteReference from "@/components/PaletteReference";
import { Subseason, colorSeasons } from "@/constants";
import { SeasonMetadata, SubseasonMetadata } from "@/app/[locale]/color-palette/colorPaletteConstants";
import Link from "next/link";
import { IconArrowRight, IconCheck, IconX } from "@tabler/icons-react";
import { beautyGuideData } from "@/app/[locale]/color-palette/makeupAndHairData";
import { seasonContentData } from "@/app/[locale]/color-palette/seasonContentData";
import { productRecommendations } from "@/app/[locale]/color-palette/productRecommendations";
import { trackProductClick, trackGizmo } from "@/lib/analytics";
import OutfitGrid from "@/components/OutfitGrid";
import StyleClubCard from "@/components/StyleClubCard";
import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import QuizPhotoUpload from "@/components/QuizPhotoUpload";
import { getDeepContent } from "@/app/[locale]/color-palette/deepContent";
import { ToneSwatch } from "@/app/[locale]/color-palette/deepContent/types";

// Map of existing comparison pages - keys are sorted alphabetically
// Palette pages that have a dedicated /outfits/[slug] page.
// Keyed by `${subSeason}-${season}`; value is the outfit page slug
// (cool-summer/cool-winter map to their common sub-season names).
const SEASON_OUTFIT_PAGES: Record<string, string> = {
  "light-spring": "light-spring",
  "true-spring": "true-spring",
  "bright-spring": "bright-spring",
  "light-summer": "light-summer",
  "true-summer": "true-summer",
  "cool-summer": "soft-summer",
  "soft-autumn": "soft-autumn",
  "true-autumn": "true-autumn",
  "deep-autumn": "deep-autumn",
  "deep-winter": "deep-winter",
  "true-winter": "true-winter",
  "cool-winter": "bright-winter",
};

const comparisonPages: Record<string, string> = {
  "spring-bright_spring-true": "/true-spring-vs-bright-spring-palette",
  "spring-bright_winter-bright": "/bright-spring-vs-bright-winter",
  "summer-cool_summer-soft": "/soft-summer-vs-cool-summer",
  "summer-cool_summer-true": "/true-summer-vs-cool-summer",
  "summer-soft_summer-true": "/true-summer-vs-soft-summer",
  "autumn-deep_autumn-soft": "/soft-autumn-vs-deep-autumn",
  "autumn-deep_autumn-true": "/deep-autumn-vs-warm-autumn",
  "autumn-soft_autumn-true": "/soft-autumn-vs-warm-autumn",
  "winter-cool_winter-deep": "/cool-winter-vs-deep-winter",
  "winter-bright_winter-deep": "/deep-winter-vs-bright-winter",
};

const getComparisonUrl = (season1: string, subSeason1: string, season2: string, subSeason2: string): string | null => {
  const s1 = season1.toLowerCase();
  const ss1 = subSeason1.toLowerCase().replace("_", "-");
  const s2 = season2.toLowerCase();
  const ss2 = subSeason2.toLowerCase().replace("_", "-");
  const pair1 = `${s1}-${ss1}`;
  const pair2 = `${s2}-${ss2}`;
  const key = [pair1, pair2].sort().join("_");
  return comparisonPages[key] || null;
};

// Celebrity data - text only for legal safety
const celebritiesBySeason: Record<string, Record<string, string[]>> = {
  SPRING: {
    LIGHT: ["Amanda Seyfried", "Gwyneth Paltrow", "Scarlett Johansson", "Dakota Fanning", "Elle Fanning", "Rachel McAdams", "Naomi Watts"],
    TRUE: ["Cameron Diaz", "Nicole Kidman", "Isla Fisher", "Heidi Klum", "Kate Hudson", "Amy Poehler", "Brittany Snow"],
    BRIGHT: ["Reese Witherspoon", "Blake Lively", "Lupita Nyong'o", "Emma Stone", "Taylor Swift", "Julianne Hough", "Dianna Agron"],
  },
  SUMMER: {
    LIGHT: ["Emily Blunt", "Amanda Peet", "Jennifer Garner", "Michelle Pfeiffer", "Kirsten Dunst", "Elle Fanning", "Sarah Jessica Parker"],
    TRUE: ["Meryl Streep", "Nicole Kidman", "Rachel McAdams", "Claire Danes", "Naomi Watts", "Helen Mirren", "Alexis Bledel"],
    COOL: ["Cate Blanchett", "Amanda Seyfried", "Gwyneth Paltrow", "Taylor Swift", "Diane Kruger", "Michelle Williams", "Saoirse Ronan"],
  },
  AUTUMN: {
    SOFT: ["Sofia Vergara", "Salma Hayek", "Penelope Cruz", "Eva Longoria", "Jessica Alba", "Rachel Weisz", "Viola Davis"],
    TRUE: ["Julianne Moore", "Debra Messing", "Eva Mendes", "Julia Roberts", "Christina Hendricks", "Bryce Dallas Howard", "Kate Walsh"],
    DEEP: ["Jennifer Aniston", "Drew Barrymore", "Kate Hudson", "Jennifer Lawrence", "Sarah Jessica Parker", "Natalie Portman", "Ellen Pompeo"],
  },
  WINTER: {
    COOL: ["Kim Kardashian", "Megan Fox", "Sandra Bullock", "Lucy Liu", "Courteney Cox", "Catherine Zeta-Jones", "Elizabeth Taylor"],
    TRUE: ["Zooey Deschanel", "Krysten Ritter", "Alexis Bledel", "Dita Von Teese", "Rooney Mara", "Audrey Hepburn", "Lucy Liu"],
    DEEP: ["Katy Perry", "Liv Tyler", "Angelina Jolie", "Emilia Clarke", "Kendall Jenner", "Megan Boone", "Megan Fox"],
  },
};

const CelebrityList = ({ season, subSeason }: { season: string; subSeason: string }) => {
  const seasonKey = season.toUpperCase();
  const subSeasonKey = subSeason.toUpperCase().replace(/-/g, "_");
  const celebrities = celebritiesBySeason[seasonKey]?.[subSeasonKey] || [];

  return (
    <SimpleGrid cols={{ base: 2, sm: 3 }} spacing={8}>
      {celebrities.map((name) => (
        <Text key={name} size="sm" c="dark.6">
          • {name}
        </Text>
      ))}
    </SimpleGrid>
  );
};

interface OutfitIdea {
  title: string;
  description: string;
  imageData: string;
}

interface ColorPaletteContentProps {
  subSeasonData: Subseason;
  season: string;
  subSeason: string;
  metadata: SubseasonMetadata;
  seasonMetadata: SeasonMetadata;
  locale: string;
}

const formatName = (text: string): string => {
  return text
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Exported for other pages that need it
interface Color {
  key: string;
  name: string;
  hex: string;
  temperature?: string;
  intensity?: string;
  undertone?: string;
}

export const ColorCard = ({ color, showHex = false }: { color: Color; showHex?: boolean }) => (
  <Box>
    <Box
      style={{
        height: 48,
        backgroundColor: color.hex,
        borderRadius: 4,
      }}
    />
    <Text ta="center" fz={11} c="dimmed" mt={4}>
      {color.name}
    </Text>
    {showHex && (
      <Text ta="center" fz={10} c="dimmed">
        {color.hex}
      </Text>
    )}
  </Box>
);

// Horizontal scroll strip for hair tones (tall gradient cards) and metals (gradient circles).
// Shared markup/logic for changes 6 & 7 — switches on `shape`.
const ToneScrollStrip = ({ items, shape }: { items: ToneSwatch[]; shape: "tall" | "circle" }) => (
  <Box style={{ position: "relative" }}>
    <Group
      gap={16}
      wrap="nowrap"
      style={{
        overflowX: "auto",
        scrollSnapType: "x mandatory",
        paddingBottom: 8,
        scrollbarWidth: "none",
      }}
      className="tone-scroll-strip"
    >
      {items.map((swatch, i) => (
        <Box key={i} style={{ flexShrink: 0, scrollSnapAlign: "start", width: shape === "tall" ? 120 : 90, textAlign: "center" }}>
          {shape === "tall" ? (
            <Box
              style={{
                width: 120,
                height: 150,
                borderRadius: 10,
                position: "relative",
                overflow: "hidden",
                background: `linear-gradient(160deg, ${swatch.hexes[0]}, ${swatch.hexes[1] || swatch.hexes[0]} 55%, ${swatch.hexes[2] || swatch.hexes[1] || swatch.hexes[0]})`,
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%)",
                }}
              />
            </Box>
          ) : (
            <Box
              style={{
                width: 90,
                height: 90,
                borderRadius: "50%",
                position: "relative",
                overflow: "hidden",
                margin: "0 auto",
                background: `linear-gradient(135deg, ${swatch.hexes[0]}, ${swatch.hexes[1] || swatch.hexes[0]} 50%, ${swatch.hexes[2] || swatch.hexes[1] || swatch.hexes[0]})`,
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.6), transparent 60%)",
                }}
              />
            </Box>
          )}
          <Text size="xs" c="dimmed" mt={8}>
            {swatch.name}
          </Text>
        </Box>
      ))}
    </Group>
    <Box
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 8,
        width: 32,
        pointerEvents: "none",
        background: "linear-gradient(to right, transparent, rgba(255,255,255,0.9))",
      }}
    />
    <style jsx>{`
      .tone-scroll-strip::-webkit-scrollbar {
        display: none;
      }
    `}</style>
  </Box>
);

// Season taglines - the emotional hook
const seasonTaglines: Record<string, Record<string, string>> = {
  SPRING: {
    LIGHT: "Warm. Delicate. Effortlessly fresh.",
    TRUE: "Warm. Clear. Naturally vibrant.",
    BRIGHT: "Warm. Bold. Unapologetically vivid.",
  },
  SUMMER: {
    LIGHT: "Cool. Soft. Quietly elegant.",
    TRUE: "Cool. Balanced. Timelessly sophisticated.",
    COOL: "Cool. Muted. Gracefully understated.",
  },
  AUTUMN: {
    SOFT: "Warm. Muted. Subtly rich.",
    TRUE: "Warm. Earthy. Deeply grounded.",
    DEEP: "Warm. Dark. Powerfully dramatic.",
  },
  WINTER: {
    COOL: "Cool. Icy. Strikingly clear.",
    TRUE: "Cool. Bold. Dramatically vivid.",
    DEEP: "Cool. Dark. Intensely sophisticated.",
  },
};

// Subseasons with generated section graphics (public/palette/{season}/{sub}/self-check.webp + capsule.webp)
const PALETTE_GRAPHIC_KEYS = new Set([
  "spring/light", "spring/true", "spring/bright",
  "summer/light", "summer/true", "summer/cool",
  "autumn/soft", "autumn/true", "autumn/deep",
  "winter/cool", "winter/true", "winter/deep",
]);

export default function ColorPaletteContent({
  subSeasonData,
  season,
  subSeason,
  metadata,
  seasonMetadata,
  locale,
}: ColorPaletteContentProps) {
  const [outfitIdeas, setOutfitIdeas] = useState<OutfitIdea[]>([]);
  const [loadingOutfits, setLoadingOutfits] = useState(true);

  const beautyGuide = beautyGuideData[season.toUpperCase()]?.[subSeason.toUpperCase().replace("-", "_")];
  const seasonContent = seasonContentData[season.toUpperCase()]?.[subSeason.toUpperCase().replace("-", "_")];
  const products = productRecommendations[season.toUpperCase()]?.[subSeason.toUpperCase().replace("-", "_")];
  const deep = getDeepContent(season, subSeason);

  const seasonDisplayName = `${formatName(subSeason)} ${formatName(season)}`;
  const tagline = seasonTaglines[season.toUpperCase()]?.[subSeason.toUpperCase().replace("-", "_")] || "";

  const seasonSlug = season.toLowerCase();
  const subSlug = subSeason.toLowerCase();
  const isSoftAutumnReference = locale === "en" && seasonSlug === "autumn" && subSlug === "soft";
  const hasGraphics = PALETTE_GRAPHIC_KEYS.has(`${seasonSlug}/${subSlug}`);

  const mergedFaqs = [
    ...(seasonContent?.faqs || []),
    ...((deep?.extraFaqs || []).filter(
      (f) => !(seasonContent?.faqs || []).some((existing) => existing.question === f.question)
    )),
  ];

  useEffect(() => {
    const fetchOutfitData = async () => {
      try {
        const docId = `${season.toUpperCase()}_${subSeason.toUpperCase().replace("-", "_")}`;
        const outfitDoc = await getDoc(doc(db, "outfitIdeas", docId));
        setOutfitIdeas(outfitDoc.data()?.ideas || []);
      } catch (error) {
        console.error("Error fetching outfit data:", error);
      } finally {
        setLoadingOutfits(false);
      }
    };
    fetchOutfitData();
  }, [season, subSeason]);

  return (
    <>
      {/* ============================================ */}
      {/* HERO - Mood board style with palette preview */}
      {/* ============================================ */}
      <Box
        style={{
          background: `linear-gradient(135deg, ${subSeasonData.primaryColors[0]?.hex || '#f5f5f5'}15, ${subSeasonData.primaryColors[3]?.hex || '#e0e0e0'}15)`,
          paddingTop: 60,
          paddingBottom: 80,
          marginBottom: 60,
        }}
      >
        <Container size="xl">
          <Box ta="center" mb={40}>
            <Text size="xs" tt="uppercase" c="dimmed" mb={16} style={{ letterSpacing: "0.15em" }}>
              Seasonal Color Palette
            </Text>
            <Title
              order={1}
              style={{
                fontSize: "clamp(2.5rem, 6vw, 4rem)",
                fontWeight: 300,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
                marginBottom: 16,
              }}
            >
              {seasonDisplayName} Color Palette
            </Title>
            <Text
              size="xl"
              c="dimmed"
              style={{
                fontWeight: 300,
                letterSpacing: "0.03em",
              }}
            >
              {tagline}
            </Text>
            {!isSoftAutumnReference && <Group justify="center" gap={12} mt={24}>
              <Anchor
                href="#upload"
                underline="never"
                onClick={() => trackGizmo("palette_hero_cta_click", { palette: `${subSeason}-${season}` })}
                style={{
                  padding: "12px 28px",
                  background: "#1a1a1a",
                  color: "white",
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Analyze My Photo — Free
              </Anchor>
              <Anchor
                component={Link}
                href="/color-analysis-quiz"
                underline="never"
                style={{
                  padding: "12px 28px",
                  border: "1px solid #1a1a1a",
                  color: "#1a1a1a",
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Take the Free Quiz
              </Anchor>
            </Group>}
          </Box>

          {/* Best colors grid - with visible names for SEO */}
          <Text fw={500} mb={16} size="sm" c="dark.6" ta="center">
            Best {seasonDisplayName} Colors
          </Text>
          <SimpleGrid cols={{ base: 4, sm: 6, md: 6 }} spacing={8}>
            {subSeasonData.primaryColors.slice(0, 12).map((color) => (
              <Box key={color.key} ta="center">
                <Box
                  style={{
                    aspectRatio: "1",
                    backgroundColor: color.hex,
                    borderRadius: 8,
                    marginBottom: 6,
                  }}
                />
                <Text size="xs" c="dimmed" style={{ fontSize: 10 }}>
                  {color.name}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
          {isSoftAutumnReference && <PaletteReference colors={subSeasonData.primaryColors} neutrals={subSeasonData.neutrals} />}
        </Container>
      </Box>

      <Container size="xl" pb={100}>
        {/* Breadcrumb */}
        <Anchor component={Link} href="/color-palette" c="dimmed" size="sm" mb={40} display="block">
          ← All Palettes
        </Anchor>

        {isSoftAutumnReference && <Text size="sm" mb="lg">Put your palette into practice: <Anchor href="/what-color-lipstick-should-i-wear">compare lipstick shades</Anchor>, <Anchor href="/what-color-blush-should-i-wear">try blush colors</Anchor>, or <Anchor href="/ai-hair-color-try-on">preview a softer hair color</Anchor>. Use the <Anchor href="/virtual-makeover">Look Studio</Anchor> to see hair and makeup together.</Text>}

        {/* Table of Contents */}
        <Box mb={60} p="md" style={{ background: "#fafafa", borderRadius: 8 }}>
          <Text size="xs" tt="uppercase" c="dimmed" mb={12} style={{ letterSpacing: "0.1em", fontWeight: 500 }}>
            On This Page
          </Text>
          <Group gap={16} wrap="wrap">
            <Anchor href="#self-check" size="sm" c="dark.6">Self-Check</Anchor>
            <Anchor href="#palette" size="sm" c="dark.6">Color Reference</Anchor>
            <Anchor href="#vs" size="sm" c="dark.6">Compare</Anchor>
            <Anchor href="#capsule" size="sm" c="dark.6">Capsule</Anchor>
            <Anchor href="#outfits" size="sm" c="dark.6">Outfits</Anchor>
            <Anchor href="#makeup" size="sm" c="dark.6">Makeup</Anchor>
            <Anchor href="#hair-color" size="sm" c="dark.6">Hair Color</Anchor>
            <Anchor href="#jewelry" size="sm" c="dark.6">Jewelry</Anchor>
            <Anchor href="#celebrities" size="sm" c="dark.6">Celebrities</Anchor>
            <Anchor href="#faq" size="sm" c="dark.6">FAQ</Anchor>
            <Anchor href="#upload" size="sm" c="dark.6">Get Analyzed</Anchor>
          </Group>
        </Box>

        {/* ============================================ */}
        {/* INTRO */}
        {/* ============================================ */}
        <Box mb={80}>
          {(() => {
            const introText = deep?.intro || metadata.description;
            const sentences = introText.split(". ").map((s, i, arr) => (i < arr.length - 1 ? `${s}.` : s));
            const lead = sentences.slice(0, 2).join(" ");
            return (
              <Text size="xl" fw={600} c="dark" style={{ lineHeight: 1.8, maxWidth: 600 }}>
                {lead}
              </Text>
            );
          })()}
        </Box>

        {/* ============================================ */}
        {/* SELF-CHECK - Key characteristics */}
        {/* ============================================ */}
        {deep?.selfCheck &&
        (deep.selfCheck.skin.length > 0 ||
          deep.selfCheck.hair.length > 0 ||
          deep.selfCheck.eyes.length > 0 ||
          deep.selfCheck.overall.length > 0) ? (
          <Box mb={80} id="self-check">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Self-Check
            </Text>
            <Title
              order={2}
              mb={32}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              Are You a {seasonDisplayName}?
            </Title>
            {!hasGraphics && deep.vibe && (
              <Stack gap={10} mb={32}>
                {([
                  ["Skin", deep.vibe.skinTones],
                  ["Hair", deep.vibe.hairTones],
                  ["Eyes", deep.vibe.eyeColors],
                ] as const).map(([label, hexes]) => (
                  <Group key={label} gap={12} wrap="nowrap">
                    <Text size="sm" fw={500} c="dark.5" style={{ width: 44, flexShrink: 0 }}>
                      {label}
                    </Text>
                    <Group gap={0} wrap="nowrap">
                      {hexes.map((hex, i) => (
                        <Box
                          key={i}
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: hex,
                            border: "1px solid rgba(0,0,0,0.08)",
                            marginLeft: i === 0 ? 0 : -8,
                          }}
                        />
                      ))}
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
            {(() => {
              const bulletsGrid = (
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={deep.vibe ? 24 : 40}>
                  {(["skin", "hair", "eyes", "overall"] as const).map((key) => {
                    const bullets = deep.selfCheck[key];
                    if (!bullets || bullets.length === 0) return null;
                    const label = key === "overall" ? "Overall Harmony" : key.charAt(0).toUpperCase() + key.slice(1);
                    return (
                      <Box key={key}>
                        <Text fw={500} mb={12} size="sm" c="dark.5">
                          {label}
                        </Text>
                        <Stack gap={deep.vibe ? 4 : 8}>
                          {bullets.map((b, i) => (
                            <Text
                              key={i}
                              size="sm"
                              c="dark.6"
                              style={{ lineHeight: deep.vibe ? 1.5 : 1.7 }}
                            >
                              • {b}
                            </Text>
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              );

              if (!hasGraphics) return bulletsGrid;

              return (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
                  {bulletsGrid}
                  <img
                    src={`/palette/${seasonSlug}/${subSlug}/self-check.webp`}
                    alt={`Typical ${seasonDisplayName} coloring — annotated skin, hair and eye guide`}
                    loading="lazy"
                    width={1400}
                    height={1867}
                    style={{ width: "100%", height: "auto", aspectRatio: "3/4", objectFit: "cover", borderRadius: 8, border: "1px solid #eee9e2" }}
                  />
                </SimpleGrid>
              );
            })()}
          </Box>
        ) : (
          seasonContent?.howToKnow?.signs && (
            <Box mb={80} id="self-check">
              <Text
                size="xs"
                tt="uppercase"
                c="dimmed"
                mb={4}
                style={{ letterSpacing: "0.15em", fontWeight: 500 }}
              >
                Self-Check
              </Text>
              <Title
                order={2}
                mb={32}
                c="dark"
                style={{
                  fontSize: "clamp(1.5rem, 3vw, 2rem)",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                }}
              >
                Are You a {seasonDisplayName}?
              </Title>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={20}>
                {seasonContent.howToKnow.signs.slice(0, 6).map((sign, i) => (
                  <Text key={i} size="md" c="dark.6" style={{ lineHeight: 1.7 }}>
                    • {sign}
                  </Text>
                ))}
              </SimpleGrid>
            </Box>
          )
        )}

        {/* ============================================ */}
        {/* VERIFY CTA - readers here self-typed from a chart */}
        {/* ============================================ */}
        <Box
          mb={60}
          p="lg"
          style={{
            borderRadius: 12,
            border: "1px solid #eee",
            background: `linear-gradient(135deg, ${subSeasonData.primaryColors[0]?.hex || "#f5f5f5"}12, #fafafa)`,
          }}
        >
          <Group justify="space-between" align="center" wrap="wrap" gap="md">
            <Box style={{ flex: 1, minWidth: 240 }}>
              <Text fw={600} size="md" mb={4}>
                Are you sure you&apos;re {seasonDisplayName}?
              </Text>
              <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
                Sister palettes look nearly identical on a chart — self-typing is where
                most people go wrong. Upload a photo and AI draping confirms your
                season, free.
              </Text>
            </Box>
            <Anchor
              href="#upload"
              underline="never"
              onClick={() => trackGizmo("palette_verify_cta_click", { palette: `${subSeason}-${season}` })}
              style={{
                padding: "10px 20px",
                background: "#1a1a1a",
                color: "white",
                borderRadius: 4,
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              Verify My Season
            </Anchor>
          </Group>
        </Box>

        {/* ============================================ */}
        {/* COLOR PALETTE - Functional reference */}
        {/* ============================================ */}
        <Box mb={80} id="palette">
          <Text
            size="xs"
            tt="uppercase"
            c="dimmed"
            mb={4}
            style={{ letterSpacing: "0.15em", fontWeight: 500 }}
          >
            Color Reference
          </Text>
          <Title
            order={2}
            mb={40}
            c="dark"
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            Your {seasonDisplayName} Neutrals — and What to Avoid
          </Title>

          {/* Neutrals — paint-chip cards */}
          <Box id="neutrals" mb={48}>
            <Text fw={500} mb={4} size="sm" c="dark.5">
              Your Neutrals
            </Text>
            <Text size="sm" c="dimmed" mb={20} style={{ lineHeight: 1.6 }}>
              The wardrobe foundation — each of these pairs with any accent in your palette.
            </Text>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                gap: 16,
              }}
            >
              {subSeasonData.neutrals.map((color) => (
                <Box key={color.key}>
                  <Box
                    style={{
                      aspectRatio: "5/4",
                      backgroundColor: color.hex,
                      borderRadius: 10,
                      border: "1px solid #e7e2db",
                    }}
                  />
                  <Text size="sm" fw={500} mt={8} style={{ lineHeight: 1.3 }}>
                    {color.name}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Colors to avoid — swap cards */}
          <Box id="avoid-colors">
            <Text fw={500} mb={4} size="sm" c="dark.5">
              Swap These
            </Text>
            <Text size="sm" c="dimmed" mb={20} style={{ lineHeight: 1.6 }}>
              The classic {seasonDisplayName} mistakes — and the shade that fixes each one.
            </Text>
            {deep?.avoidDetails && deep.avoidDetails.length > 0 ? (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={20}>
                {deep.avoidDetails.map((item, i) => (
                  <Box
                    key={i}
                    style={{
                      border: "1px solid #eee9e2",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <Box
                      style={{
                        position: "relative",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        height: 84,
                      }}
                    >
                      <Box style={{ backgroundColor: item.hex }} />
                      <Box style={{ backgroundColor: item.swapHex }} />
                      <Box
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 14,
                          color: "#1a1a1a",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                        }}
                      >
                        →
                      </Box>
                    </Box>
                    <Box p={14}>
                      <Text size="sm" style={{ lineHeight: 1.4 }}>
                        <Text span c="dimmed" td="line-through">{item.name}</Text>
                        <Text span c="dimmed">{" → "}</Text>
                        <Text span fw={600}>{item.swapFor}</Text>
                      </Text>
                      <Text size="xs" c="dimmed" mt={6} style={{ lineHeight: 1.5 }}>
                        {item.reason}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                  gap: 16,
                }}
              >
                {subSeasonData.avoidColors.map((color) => (
                  <Box key={color.key}>
                    <Box
                      style={{
                        aspectRatio: "5/4",
                        backgroundColor: color.hex,
                        borderRadius: 10,
                        border: "1px solid #e7e2db",
                        opacity: 0.75,
                      }}
                    />
                    <Text size="sm" fw={500} mt={8} c="dimmed" td="line-through">
                      {color.name}
                    </Text>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Photo upload CTA */}
        <Box mb={80} id="upload">
          <Text
            size="xs"
            tt="uppercase"
            c="dimmed"
            mb={24}
            style={{ letterSpacing: "0.15em", fontWeight: 500 }}
          >
            Not Sure If You're {seasonDisplayName}?
          </Text>
          <QuizPhotoUpload
            quizName={`${seasonDisplayName} Palette`}
            headline={isSoftAutumnReference ? undefined : "Get your exact colors with AI analysis"}
            subtext={isSoftAutumnReference ? undefined : "Upload a photo and we'll confirm your season + give you 50 personalized shades."}
          />
        </Box>

        {/* ============================================ */}
        {/* COMPARISONS - vs adjacent seasons */}
        {/* ============================================ */}
        {deep?.comparisons && deep.comparisons.length > 0 && (
          <Box mb={80} id="vs">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Adjacent Seasons
            </Text>
            <Title
              order={2}
              mb={32}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              {seasonDisplayName} vs. Its Neighbors
            </Title>
            <Stack gap={40}>
              {deep.comparisons.map((comp, i) => (
                <Box key={i}>
                  <Anchor component={Link} href={comp.vsHref} fw={600} size="md" mb={16} display="block" c="dark">
                    {seasonDisplayName} vs {comp.vsLabel} →
                  </Anchor>
                  <Box style={{ overflowX: "auto" }}>
                    <Box component="table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                      <Box component="thead">
                        <Box component="tr" style={{ borderBottom: "1px solid #eee" }}>
                          <Box component="th" style={{ textAlign: "left", padding: "8px 12px", fontWeight: 500, color: "#888" }}>Attribute</Box>
                          <Box component="th" style={{ textAlign: "left", padding: "8px 12px", fontWeight: 500, color: "#888" }}>{seasonDisplayName}</Box>
                          <Box component="th" style={{ textAlign: "left", padding: "8px 12px", fontWeight: 500, color: "#888" }}>{comp.vsLabel}</Box>
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {comp.rows.map((row, j) => (
                          <Box component="tr" key={j} style={{ borderBottom: "1px solid #f5f5f5" }}>
                            <Box component="td" style={{ padding: "8px 12px", fontWeight: 500, color: "#1a1a1a" }}>{row.attribute}</Box>
                            <Box component="td" style={{ padding: "8px 12px" }}>
                              {row.thisHex && (
                                <Box
                                  component="span"
                                  style={{
                                    display: "inline-block",
                                    width: 14,
                                    height: 14,
                                    borderRadius: 4,
                                    backgroundColor: row.thisHex,
                                    marginRight: 6,
                                    verticalAlign: "middle",
                                  }}
                                />
                              )}
                              {row.thisSeason}
                            </Box>
                            <Box component="td" style={{ padding: "8px 12px", color: "#666" }}>
                              {row.otherHex && (
                                <Box
                                  component="span"
                                  style={{
                                    display: "inline-block",
                                    width: 14,
                                    height: 14,
                                    borderRadius: 4,
                                    backgroundColor: row.otherHex,
                                    marginRight: 6,
                                    verticalAlign: "middle",
                                  }}
                                />
                              )}
                              {row.otherSeason}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* ============================================ */}
        {/* CAPSULE WARDROBE */}
        {/* ============================================ */}
        {deep?.capsule && deep.capsule.length > 0 && (
          <Box mb={80} id="capsule">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Capsule Wardrobe
            </Text>
            <Title
              order={2}
              mb={32}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              A 10-Piece {seasonDisplayName} Capsule
            </Title>
            {hasGraphics && (
              <img
                src={`/palette/${seasonSlug}/${subSlug}/capsule.webp`}
                alt={`${seasonDisplayName} 10-piece capsule wardrobe — illustrated flat lay`}
                loading="lazy"
                width={1400}
                height={1050}
                style={{ width: "100%", height: "auto", aspectRatio: "4/3", objectFit: "cover", borderRadius: 8, marginBottom: 24, border: "1px solid #eee9e2" }}
              />
            )}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={16}>
              {deep.capsule.map((piece, i) => (
                <Group key={i} gap={12} wrap="nowrap">
                  <Box style={{ width: 36, height: 36, backgroundColor: piece.hex, borderRadius: 6, flexShrink: 0 }} />
                  <Box>
                    <Text size="sm" fw={500} c="dark.7">{piece.item}</Text>
                    <Text size="xs" c="dimmed">{piece.color}</Text>
                  </Box>
                </Group>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* ============================================ */}
        {/* OUTFITS */}
        {/* ============================================ */}
        <Box mb={80} id="outfits">
          <Text
            size="xs"
            tt="uppercase"
            c="dimmed"
            mb={4}
            style={{ letterSpacing: "0.15em", fontWeight: 500 }}
          >
            Outfits
          </Text>
          <Title
            order={2}
            mb={8}
            c="dark"
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            {seasonDisplayName} Outfit Ideas
          </Title>
          {SEASON_OUTFIT_PAGES[`${subSeason}-${season}`.toLowerCase()] && (
            <Text size="sm" c="dimmed" mb={24}>
              Want more?{" "}
              <Anchor
                component={Link}
                href={`/outfits/${SEASON_OUTFIT_PAGES[`${subSeason}-${season}`.toLowerCase()]}`}
                fw={500}
              >
                See 16 complete {seasonDisplayName} outfits
              </Anchor>{" "}
              with photos, piece lists, and a capsule wardrobe.
            </Text>
          )}

          {loadingOutfits ? (
            <Text c="dimmed" ta="center" py={60}>
              Loading outfit inspiration...
            </Text>
          ) : outfitIdeas.length > 0 ? (
            <OutfitGrid outfitIdeas={outfitIdeas} />
          ) : (
            <Text c="dimmed" ta="center" py={60}>
              Outfit inspiration coming soon.
            </Text>
          )}

          <Box mt={32}>
            <StyleClubCard season={seasonDisplayName} />
          </Box>
        </Box>

        {/* ============================================ */}
        {/* MAKEUP */}
        {/* ============================================ */}
        {beautyGuide && (
          <Box mb={80} id="makeup">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Makeup
            </Text>
            <Title
              order={2}
              mb={24}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              Best {seasonDisplayName} Makeup Colors
            </Title>

            {seasonContent?.makeupIntro && (
              <Text c="dark.6" mb={40} style={{ lineHeight: 1.8, maxWidth: 600 }}>
                {seasonContent.makeupIntro}
              </Text>
            )}

            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={32} mb={48}>
              {/* Lips */}
              <Box id="lipstick-colors">
                <Text fw={500} mb={16} size="sm" c="dark.5">
                  Lipstick Shades
                </Text>
                <Group gap={8}>
                  {beautyGuide.makeup.lipsticks.map((color, i) => (
                    <Box key={i} style={{ textAlign: "center" }}>
                      <Box
                        style={{
                          width: 36,
                          height: 36,
                          backgroundColor: color.hex,
                          borderRadius: "50%",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                        title={color.name}
                      />
                      <Text size="xs" c="dimmed" mt={6} style={{ fontSize: 10 }}>
                        {color.name}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Box>

              {/* Eyes */}
              <Box id="eyeshadow-colors">
                <Text fw={500} mb={16} size="sm" c="dark.5">
                  Eyeshadow Tones
                </Text>
                <Group gap={8}>
                  {beautyGuide.makeup.eyeshadows.map((color, i) => (
                    <Box key={i} style={{ textAlign: "center" }}>
                      <Box
                        style={{
                          width: 36,
                          height: 36,
                          backgroundColor: color.hex,
                          borderRadius: 6,
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                        title={color.name}
                      />
                      <Text size="xs" c="dimmed" mt={6} style={{ fontSize: 10 }}>
                        {color.name}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Box>

              {/* Cheeks */}
              <Box id="blush-colors">
                <Text fw={500} mb={16} size="sm" c="dark.5">
                  Blush Shades
                </Text>
                <Group gap={8}>
                  {beautyGuide.makeup.blushes.map((color, i) => (
                    <Box key={i} style={{ textAlign: "center" }}>
                      <Box
                        style={{
                          width: 36,
                          height: 36,
                          backgroundColor: color.hex,
                          borderRadius: "50%",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                        title={color.name}
                      />
                      <Text size="xs" c="dimmed" mt={6} style={{ fontSize: 10 }}>
                        {color.name}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Box>
            </SimpleGrid>

            {/* Lipstick Product Recommendations */}
            {products?.lipsticks && products.lipsticks.length > 0 && (
              <Box mt={48} id="lipstick-recommendations">
                <Text fw={500} mb={20} size="sm" c="dark.5">
                  Recommended Lipsticks
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={24}>
                  {products.lipsticks.map((product, i) => (
                    <Anchor
                      key={i}
                      href={product.amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="never"
                      onClick={() =>
                        trackProductClick(product.name, product.brand, "lipstick", `${subSeason} ${season}`)
                      }
                    >
                      <Box
                        p="md"
                        style={{
                          borderRadius: 8,
                          border: "1px solid #eee",
                          transition: "box-shadow 0.2s",
                        }}
                        className="product-card"
                      >
                        <Group gap="md" align="flex-start">
                          {product.imageUrl && (
                            <img
                              src={product.imageUrl}
                              alt={product.name}
                              style={{ width: 60, height: 60, objectFit: "contain" }}
                              loading="lazy"
                            />
                          )}
                          <Box style={{ flex: 1 }}>
                            <Text size="sm" fw={500} c="dark">
                              {product.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {product.brand}
                            </Text>
                            <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
                              {product.description}
                            </Text>
                          </Box>
                        </Group>
                      </Box>
                    </Anchor>
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </Box>
        )}

        {/* ============================================ */}
        {/* HAIR COLOR */}
        {/* ============================================ */}
        {beautyGuide && (
          <Box mb={80} id="hair-color">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Hair Color
            </Text>
            <Title
              order={2}
              mb={24}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              Best Hair Colors for {seasonDisplayName}
            </Title>

            {isSoftAutumnReference && <Box id="soft-autumn-hair-colors" mb={40}>
              <Text mb="md">For Soft Autumn hair colors, compare gentle warmth and muted contrast first. A natural brown or soft bronde can be a useful reference before brighter copper, very ashy blonde or near-black. Your preferred depth still matters.</Text>
              <HairShadeShortlist source="soft-autumn-hair" ids={["hazelnut-brown", "bronde", "light-auburn"]} notes={[
                "A softly warm brunette direction. Compare with your natural brown to judge how much golden reflect you enjoy.",
                "A brown-blonde direction for a lighter change. Compare the contrast with your brows as well as the warmth.",
                "A muted red-brown direction. Compare with a brighter copper to decide how much saturation you want.",
              ]} />
              <Text size="sm">Soft Autumn favors muted warmth in the seasonal framework; it does not require everyone to dye their hair. If cool, muted colors feel easier to wear, compare <Anchor href="/color-palette/comparison/soft-autumn-vs-soft-summer">Soft Autumn and Soft Summer</Anchor>. If stronger golden tones work better, compare <Anchor href="/soft-autumn-vs-warm-autumn">Soft Autumn and Warm Autumn</Anchor>.</Text>
            </Box>}

            {deep?.hairToneSwatches && deep.hairToneSwatches.length > 0 && (
              <Box mb={40}>
                <ToneScrollStrip items={deep.hairToneSwatches} shape="tall" />
              </Box>
            )}

            {seasonContent?.hairIntro && (
              <Text c="dark.6" mb={40} style={{ lineHeight: 1.8, maxWidth: 600 }}>
                {seasonContent.hairIntro}
              </Text>
            )}

            <SimpleGrid cols={2} spacing={32} mb={48}>
              <Box id="best-hair-colors">
                <Group gap="xs" mb={16}>
                  <IconCheck size={16} color="green" />
                  <Text fw={500} size="sm" c="dark.5">
                    Best Hair Tones
                  </Text>
                </Group>
                <Group gap={12}>
                  {beautyGuide.hair.ideal.map((color, i) => (
                    <Box key={i} style={{ textAlign: "center" }}>
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          backgroundColor: color.hex,
                          borderRadius: 6,
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Text size="xs" c="dimmed" mt={6}>
                        {color.name}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Box>
              <Box id="avoid-hair-colors">
                <Group gap="xs" mb={16}>
                  <IconX size={16} color="#999" />
                  <Text fw={500} size="sm" c="dimmed">
                    Hair Tones to Avoid
                  </Text>
                </Group>
                <Group gap={12}>
                  {beautyGuide.hair.avoid.map((color, i) => (
                    <Box key={i} style={{ textAlign: "center" }}>
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          backgroundColor: color.hex,
                          borderRadius: 6,
                          opacity: 0.6,
                        }}
                      />
                      <Text size="xs" c="dimmed" mt={6}>
                        {color.name}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Box>
            </SimpleGrid>

            {/* Hair Product Recommendations */}
            {products?.hairColors && products.hairColors.length > 0 && (
              <Box mt={48} id="hair-color-recommendations">
                <Text fw={500} mb={20} size="sm" c="dark.5">
                  Recommended Hair Glosses
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24}>
                  {products.hairColors.map((product, i) => (
                    <Anchor
                      key={i}
                      href={product.amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="never"
                      onClick={() =>
                        trackProductClick(product.name, product.brand, "hair", `${subSeason} ${season}`)
                      }
                    >
                      <Box
                        p="md"
                        style={{
                          borderRadius: 8,
                          border: "1px solid #eee",
                          transition: "box-shadow 0.2s",
                        }}
                        className="product-card"
                      >
                        <Group gap="md" align="flex-start">
                          {product.imageUrl && (
                            <img
                              src={product.imageUrl}
                              alt={product.name}
                              style={{ width: 60, height: 60, objectFit: "contain" }}
                              loading="lazy"
                            />
                          )}
                          <Box style={{ flex: 1 }}>
                            <Text size="sm" fw={500} c="dark">
                              {product.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {product.brand}
                            </Text>
                            <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
                              {product.description}
                            </Text>
                          </Box>
                        </Group>
                      </Box>
                    </Anchor>
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Shopping Tips */}
            {products?.shoppingTips && products.shoppingTips.length > 0 && (
              <Box mt={40}>
                <Text fw={500} mb={16} size="sm" c="dark.5">
                  Shopping Tips
                </Text>
                <Stack gap={8}>
                  {products.shoppingTips.map((tip, i) => (
                    <Text key={i} size="sm" c="dark.6" style={{ lineHeight: 1.6 }}>
                      • {tip}
                    </Text>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* ============================================ */}
        {/* JEWELRY METALS */}
        {/* ============================================ */}
        {beautyGuide && (
          <Box mb={80} id="jewelry">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Jewelry
            </Text>
            <Title
              order={2}
              mb={40}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              Best Metals for {seasonDisplayName}
            </Title>
            {deep?.metals && deep.metals.length > 0 && (
              <Box mb={40}>
                <ToneScrollStrip items={deep.metals} shape="circle" />
              </Box>
            )}
            <SimpleGrid cols={2} spacing={32}>
              <Box id="best-metals">
                <Text fw={500} mb={16} size="sm" c="dark.5">
                  Best Metals
                </Text>
                <Group gap={16}>
                  {beautyGuide.makeup.metals.best.map((color, i) => (
                    <Box key={i} style={{ textAlign: "center" }}>
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          backgroundColor: color.hex,
                          borderRadius: "50%",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Text size="xs" c="dimmed" mt={8}>
                        {color.name}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Box>
              <Box id="avoid-metals">
                <Text fw={500} mb={16} size="sm" c="dimmed">
                  Metals to Avoid
                </Text>
                <Group gap={16}>
                  {beautyGuide.makeup.metals.avoid.map((color, i) => (
                    <Box key={i} style={{ textAlign: "center" }}>
                      <Box
                        style={{
                          width: 44,
                          height: 44,
                          backgroundColor: color.hex,
                          borderRadius: "50%",
                          opacity: 0.6,
                        }}
                      />
                      <Text size="xs" c="dimmed" mt={8}>
                        {color.name}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Box>
            </SimpleGrid>
          </Box>
        )}

        {/* ============================================ */}
        {/* CELEBRITIES */}
        {/* ============================================ */}
        {deep?.celebrities && deep.celebrities.length > 0 ? (
          <Box mb={80} id="celebrities">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Celebrities
            </Text>
            <Title
              order={2}
              mb={24}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              {seasonDisplayName} Celebrities
            </Title>
            <Text c="dark.6" mb={32} style={{ lineHeight: 1.7 }}>
              These celebrities are commonly typed as {seasonDisplayName} due to their coloring characteristics.
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing={24}>
              {deep.celebrities.map((celeb, i) => (
                <Box key={i}>
                  <img
                    src={celeb.imageUrl}
                    loading="lazy"
                    alt={`${celeb.name} — ${seasonDisplayName} celebrity example`}
                    referrerPolicy="no-referrer"
                    style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 8, marginBottom: 8 }}
                  />
                  <Anchor href={celeb.wikipediaUrl} target="_blank" rel="noopener noreferrer" size="sm" fw={500} c="dark" underline="hover">
                    {celeb.name}
                  </Anchor>
                  <Text size="xs" c="dimmed" mt={4} style={{ lineHeight: 1.5 }}>{celeb.why}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <Text size="xs" c="dimmed" mt={16}>Photos via Wikipedia.</Text>
          </Box>
        ) : (
          <Box mb={80} id="celebrities">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              Celebrities
            </Text>
            <Title
              order={2}
              mb={24}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              {seasonDisplayName} Celebrities
            </Title>
            <Text c="dark.6" mb={24} style={{ lineHeight: 1.7 }}>
              These celebrities are commonly typed as {seasonDisplayName} due to their coloring characteristics.
              Looking at {seasonDisplayName.toLowerCase()} celebrities can help you understand how these colors
              look on real people and give you style inspiration.
            </Text>
            <CelebrityList season={season} subSeason={subSeason} />
          </Box>
        )}

        {/* ============================================ */}
        {/* FAQ */}
        {/* ============================================ */}
        {mergedFaqs.length > 0 && (
          <Box mb={80} id="faq">
            <Text
              size="xs"
              tt="uppercase"
              c="dimmed"
              mb={4}
              style={{ letterSpacing: "0.15em", fontWeight: 500 }}
            >
              FAQ
            </Text>
            <Title
              order={2}
              mb={40}
              c="dark"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "-0.01em",
              }}
            >
              {seasonDisplayName} Questions, Answered
            </Title>
            <Stack gap={32}>
              {mergedFaqs.map((faq, i) => (
                <Box key={i}>
                  <Text fw={500} mb={12} size="md">
                    {faq.question}
                  </Text>
                  <Text size="md" c="dark.6" style={{ lineHeight: 1.8 }}>
                    {faq.answer}
                  </Text>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* ============================================ */}
        {/* COMPARE SEASONS - Internal links for SEO */}
        {/* ============================================ */}
        <Box mb={80} id="compare">
          <Text
            size="xs"
            tt="uppercase"
            c="dimmed"
            mb={4}
            style={{ letterSpacing: "0.15em", fontWeight: 500 }}
          >
            Explore
          </Text>
          <Title
            order={2}
            mb={16}
            c="dark"
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2rem)",
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            Compare Every Season
          </Title>
          <Text c="dark.6" mb={32} style={{ lineHeight: 1.7 }}>
            Confused about whether you're {seasonDisplayName} or a similar season? Compare the key
            differences to find your true colors.
          </Text>

          <Stack gap={16} mb={32}>
            {Object.entries(colorSeasons[season.toUpperCase()].subseasons).map(([otherSubSeason]) => {
              if (otherSubSeason === subSeason.toUpperCase().replace("-", "_")) return null;
              const otherSubSeasonSlug = otherSubSeason.toLowerCase().replace("_", "-");
              const otherSubSeasonName = formatName(otherSubSeason.replace("_", " "));
              const comparisonUrl = getComparisonUrl(season, subSeason, season, otherSubSeason);

              return (
                <Card key={otherSubSeason} withBorder padding="md" radius="md">
                  <Text fw={600} size="sm" mb={4}>
                    {seasonDisplayName} vs {otherSubSeasonName} {formatName(season)}
                  </Text>
                  <Group gap={16}>
                    <Anchor
                      component={Link}
                      href={`/color-palette/${season}/${otherSubSeasonSlug}`}
                      size="sm"
                      c="teal"
                    >
                      View {otherSubSeasonName} {formatName(season)} palette →
                    </Anchor>
                    {comparisonUrl && (
                      <Anchor
                        component={Link}
                        href={comparisonUrl}
                        size="sm"
                        c="teal"
                      >
                        See detailed comparison →
                      </Anchor>
                    )}
                  </Group>
                </Card>
              );
            })}
          </Stack>

          {/* Cross-season comparisons if available */}
          {(() => {
            const crossSeasonComparisons = Object.keys(colorSeasons).flatMap((otherSeason) => {
              if (otherSeason === season.toUpperCase()) return [];
              return Object.keys(colorSeasons[otherSeason].subseasons).map((otherSub) => {
                const comparisonUrl = getComparisonUrl(season, subSeason, otherSeason, otherSub);
                if (!comparisonUrl) return null;
                const otherSubName = formatName(otherSub.replace("_", " "));
                return {
                  key: `${otherSeason}-${otherSub}`,
                  url: comparisonUrl,
                  name: `${otherSubName} ${formatName(otherSeason)}`,
                };
              }).filter(Boolean);
            });

            if (crossSeasonComparisons.length === 0) return null;

            return (
              <>
                <Text fw={500} mb={16} size="sm" c="dark.5">
                  Cross-Season Comparisons
                </Text>
                <Stack gap={12}>
                  {crossSeasonComparisons.map((comparison) => (
                    <Card key={comparison!.key} withBorder padding="sm" radius="md">
                      <Group justify="space-between">
                        <Text fw={500} size="sm">
                          {seasonDisplayName} vs {comparison!.name}
                        </Text>
                        <Anchor
                          component={Link}
                          href={comparison!.url}
                          size="sm"
                          c="teal"
                        >
                          Compare →
                        </Anchor>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </>
            );
          })()}

          <Text mt={32} size="sm">
            Also explore your <Anchor component={Link} href="/kibbe" c="teal">Kibbe body type →</Anchor>
          </Text>
        </Box>

        {/* ============================================ */}
        {/* FINAL CTA - Upload form */}
        {/* ============================================ */}
        <Box py={60}>
          <QuizPhotoUpload
            quizName={`${seasonDisplayName} Palette Bottom`}
            headline={isSoftAutumnReference ? undefined : "Find your true season"}
            subtext={isSoftAutumnReference ? undefined : "Upload a photo for personalized color analysis with 50+ shades tailored to you."}
          />
        </Box>
      </Container>

      {/* Mobile sticky CTA */}
      <Box
        hiddenFrom="md"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "12px 16px",
          background: "white",
          borderTop: "1px solid #eee",
          zIndex: 1000,
        }}
      >
        <Anchor
          href="#upload"
          underline="never"
          style={{
            display: "block",
            width: "100%",
            padding: "10px 16px",
            background: "#1a1a1a",
            color: "white",
            textAlign: "center",
            borderRadius: 4,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Get My Colors Analyzed
        </Anchor>
      </Box>
      <Box hiddenFrom="md" h={60} />

      {/* CSS for hover effects */}
      <style jsx global>{`
        .product-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
      `}</style>
    </>
  );
}
